type PhotoCropInput = {
	width: number;
	height: number;
	xOffset: number;
	yOffset: number;
};

/**
 * Maps a `MediaCropArea` (structurally — shared/utils can't import the shared/hooks layer, so
 * this only depends on the fields it actually needs) into the `{width, height, xOffset, yOffset}`
 * shape user-service's `photoCrop` expects: a square crop sized to the image's smaller natural
 * dimension, offset into the larger one.
 *
 * Offsets are clamped so `offset + size` can never exceed the source image's own rounded natural
 * dimension. The cropper reports `x`/`y` as floats that can drift a fraction of a pixel past the
 * exact pannable boundary — `Math.round()` alone doesn't guard against that drift landing on the
 * far side of an integer, and user-service rejects an out-of-bounds crop as a generic
 * invalid-parameters error with no indication of which field caused it.
 */
export function toPhotoCropInput(crop: { w: number; h: number; x: number; y: number }): PhotoCropInput {
	const naturalWidth = Math.round(crop.w);
	const naturalHeight = Math.round(crop.h);
	const size = Math.min(naturalWidth, naturalHeight);
	const maxXOffset = Math.max(0, naturalWidth - size);
	const maxYOffset = Math.max(0, naturalHeight - size);

	return {
		width: size,
		height: size,
		xOffset: crop.x > 0 ? Math.min(Math.round(crop.x), maxXOffset) : 0,
		yOffset: crop.y > 0 ? Math.min(Math.round(crop.y), maxYOffset) : 0,
	};
}
